<script lang="ts">
  import { tick } from 'svelte';

  import { ArtworkLink } from '@shared/ui/links/artwork-link';
  import { theme } from '@shared/ui/theme';
  import { cn, is } from '@shared/utils';

  export let collapseHeight = 160; // max visible height for collapsed ReadMore component

  export let readMoreText = 'Read more';
  export let readLessText = 'Read less';
  export let position: 'left' | 'center' | 'right' = 'left';

  let collapsibleElement: HTMLElement;

  const positions = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  let innerHeight: number;

  let isReadmoreEnabled = false;
  let isAnimated = false;
  let isShown = false;

  const handleClick = async () => {
    isShown = !isShown;
    if (!isShown) {
      await tick();
      collapsibleElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const wrapperClasses = {
    all: 'relative grid overflow-hidden after:z-[1] after:absolute after:w-full after:h-32 after:bottom-0 after:left-0 after:pointer-events-none',
    animation: 'transition-[grid-template-rows] duration-0',
  };

  const checkAndEnableReadmore = (innerHeight: number) => {
    if (innerHeight === undefined) return;
    if (innerHeight >= collapseHeight) {
      isReadmoreEnabled = true;
      setTimeout(() => (isAnimated = true), 0); // avoid on mount animation
    } else {
      isReadmoreEnabled = false;
      isAnimated = false;
    }
  };

  $: checkAndEnableReadmore(innerHeight);

  $: isDarkTheme = $theme === 'dark';
  $: isShownGradient = isReadmoreEnabled && !isShown;
</script>

<div
  bind:this="{collapsibleElement}"
  class="{cn(
    wrapperClasses.all,
    {
      'bg-custom-light': isShownGradient && !isDarkTheme,
      'bg-custom-dark': isShownGradient && isDarkTheme,
    },
    is(isAnimated, wrapperClasses.animation),
  )}
    "
  style="
    max-height: {isReadmoreEnabled ? 'unset' : `${collapseHeight}px`};
    grid-template-rows: {!isReadmoreEnabled
    ? `repeat(1, minmax(${0}px, 1fr))`
    : isShown
      ? `repeat(1, minmax(${collapseHeight}px, 1fr))`
      : `repeat(1, minmax(${collapseHeight}px, 0fr))`}
    "
>
  <div bind:clientHeight="{innerHeight}" class="relative mb-4 h-fit">
    <slot />
  </div>
</div>

<div class="{is(!isReadmoreEnabled, 'hidden')} mt-2 flex {positions[position]}">
  <ArtworkLink on:click="{handleClick}">
    {!isShown ? readMoreText : readLessText}
  </ArtworkLink>
</div>

<style lang="scss">
  .bg-custom-light::after {
    background: linear-gradient(to top, #ffffff, transparent);
  }

  .bg-custom-dark::after {
    background: linear-gradient(to top, #0d0d0d, transparent);
  }
</style>
